import { REHYDRATE } from "redux-persist";
import { ActionTypes } from "../actions/search";
import { hasPosition } from "../../utils/calc";

export const DEFAULT_VIEWPORT = {
  center: {
    lat: 51.165691,
    lng: 10.451526,
  },
  zoom: 9,
};

export const DEFAULT_FILTERS = {
  locationTypes: ["on_site", "online", "home", "by_arrangement"],
  bookable: true,
  ageGroups: ["baby", "child", "parents"],
  categorySpaces: [],
  radius: 30,
};

export const initialState = Object.freeze({
  address: null,
  term: null,
  center: null,
  results: [],
  metaData: null,
  isLoading: false,
  listView: false,
  locations: null,
  coursesResults: [],
  coursesMetaData: null,
  filters: DEFAULT_FILTERS,
});

// Clear 'toddler' from persisted filter age groups
const clearedAgeGroups = rehydratedState =>
  ((rehydratedState || {}).filters || DEFAULT_FILTERS).ageGroups.filter(key => key !== "toddler");

export default (state = initialState, action) => {
  const { type, payload = {} } = action;

  switch (type) {
    case ActionTypes.SEARCH_REQUEST: {
      return {
        ...state,
        isLoading: true,
        locations: payload,
      };
    }

    case ActionTypes.SEARCH_FAILED: {
      return {
        ...state,
        isLoading: false,
        metaData: null,
        coursesMetaData: null,
      };
    }

    case ActionTypes.SEARCH_CLEARED: {
      return {
        ...state,
        results: [],
        metaData: null,
        isLoading: false,
        coursesResults: [],
        coursesMetaData: null,
      };
    }

    // One response holds every offering in the searched area, so a later one replaces the
    // markers rather than adding to them.
    case ActionTypes.SEARCH_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        term: payload.term,
        results: payload.results || [],
        // Save all infos besides results for later display controls
        metaData: { ...payload, results: null },
      };
    }

    case ActionTypes.COURSE_SEARCH_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        term: payload.term,
        // Append results if offset is bigger than results before
        coursesResults:
          payload.page > ((state.coursesMetaData || {}).page || 1)
            ? (state.coursesResults || []).concat(payload.results)
            : payload.results || [],
        // Save all infos besides results for later display controls
        coursesMetaData: { ...payload, results: null },
      };
    }

    case ActionTypes.ADDRESS_CLEARED: {
      return {
        ...state,
        address: null,
        center: initialState.center,
        // Both searches answered for the position that was just given up
        results: [],
        metaData: null,
        coursesResults: [],
        coursesMetaData: null,
      };
    }

    // Called without coordinates this clears the position rather than setting one made of
    // undefined, which every consumer down to Leaflet would take for a real place.
    case ActionTypes.ADDRESS_CHANGED: {
      return {
        ...state,
        address: payload.address || null,
        center: hasPosition(payload) ? { lat: payload.lat, lng: payload.lng } : null,
      };
    }

    case ActionTypes.LIST_VIEW_CHANGED: {
      return {
        ...state,
        listView: payload,
      };
    }

    case ActionTypes.SET_FILTERS: {
      return {
        ...state,
        filters: payload,
      };
    }

    case ActionTypes.SET_FILTER: {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...payload,
        },
      };
    }

    case REHYDRATE: {
      const rehydratedState = payload.search || {};

      return {
        ...initialState,
        ...rehydratedState,
        isLoading: false,
        // The map opens on the map, never on the results sheet
        listView: false,
        metaData: null,
        coursesMetaData: null,
        // Defaults first, so filters added after a visitor's state was persisted still get a value
        filters: {
          ...DEFAULT_FILTERS,
          ...((rehydratedState || {}).filters || {}),
          ageGroups: clearedAgeGroups(rehydratedState),
        },
      };
    }

    default:
      return state;
  }
};
